/* ==========================================================================
   AUSHÉ — custom.js

   1. ACCORDION      Smooth open/close animation for <details> elements
                     inside any .accordion block (product page tabs, FAQ,
                     collapsible rows).

   2. CART DRAWER    Injects a "CART • N" label into the Qikify slide cart
                     header (.qsc2-drawer-header), and keeps the header cart
                     bubble count in sync.

   Nothing else in the theme depends on this file.
   ========================================================================== */

(function () {
  'use strict';

  /* ========================================================================
     1. ACCORDION
     ======================================================================== */

  var ANIM_MS = 300; // must stay >= the CSS transition on .accordion__content
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initAccordion(details) {
    // Guard against double-binding when a section re-renders in the editor
    if (details.dataset.ausheAccordion === 'true') return;

    var summary = details.querySelector('summary');
    var content = details.querySelector('.accordion__content');
    if (!summary || !content) return;

    details.dataset.ausheAccordion = 'true';

    var animating = false;

    summary.addEventListener('click', function (event) {
      event.preventDefault();
      if (animating) return;

      // Respect the visitor's OS motion setting — just toggle, no animation
      if (reducedMotion.matches) {
        if (details.hasAttribute('open')) {
          details.removeAttribute('open');
        } else {
          details.setAttribute('open', '');
        }
        return;
      }

      animating = true;

      if (details.hasAttribute('open')) {
        // --- Closing ---
        content.style.height = content.scrollHeight + 'px';
        void content.offsetHeight; // force reflow (needed on iOS Safari)

        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            content.style.height = '0px';
            content.style.opacity = '0';
          });
        });

        window.setTimeout(function () {
          details.removeAttribute('open');
          content.style.height = '';
          content.style.opacity = '';
          animating = false;
        }, ANIM_MS + 20);
      } else {
        // --- Opening ---
        details.setAttribute('open', '');
        var targetHeight = content.scrollHeight;

        content.style.height = '0px';
        content.style.opacity = '0';
        void content.offsetHeight; // force reflow (needed on iOS Safari)

        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            content.style.height = targetHeight + 'px';
            content.style.opacity = '1';
          });
        });

        window.setTimeout(function () {
          content.style.height = ''; // release to auto so nested content can grow
          animating = false;
        }, ANIM_MS + 20);
      }
    });
  }

  function initAllAccordions(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('.accordion details');
    Array.prototype.forEach.call(nodes, initAccordion);
  }

  /* ========================================================================
     2. CART COUNT — header bubble + Qikify drawer header label
     ======================================================================== */

  var DRAWER_HEADER = '.qsc2-drawer-header';
  var LABEL_CLASS = 'cart-text-header-label';

  var cartCount = null;
  var fetching = false;

  // Keeps the number in the header cart bubble in sync. Needed because the
  // Qikify slide cart does not re-render Shopify's cart-icon-bubble section.
  function syncBubble() {
    if (cartCount === null) return;

    var bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span');
    if (bubble && bubble.textContent.trim() !== String(cartCount)) {
      bubble.textContent = cartCount;
    }

    var srText = document.querySelector('#cart-icon-bubble .visually-hidden');
    if (srText) srText.textContent = 'Cart (' + cartCount + ' items)';
  }

  function paintLabel() {
    var header = document.querySelector(DRAWER_HEADER);
    if (!header || cartCount === null) return;

    var label = header.querySelector('.' + LABEL_CLASS);
    if (!label) {
      label = document.createElement('span');
      label.className = LABEL_CLASS;
      header.insertBefore(label, header.firstChild);
    }

    var text = 'CART • ' + cartCount;
    if (label.textContent !== text) label.textContent = text;
  }

  function refreshCount() {
    if (fetching) return;
    fetching = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        cartCount = cart.item_count;
      })
      .catch(function () {
        if (cartCount === null) cartCount = 0;
      })
      .then(function () {
        fetching = false;
        syncBubble();
        paintLabel();
      });
  }

  /* ========================================================================
     WIRING
     ======================================================================== */

  // One debounced observer handles both features.
  //
  // Two deliberate safety measures, because this page runs Judge.me, Qikify
  // and other apps that mutate the DOM more or less continuously:
  //   1. 250ms debounce, so a chatty app cannot pin us to every frame.
  //   2. We disconnect while we do our own DOM writes, so this can never
  //      re-trigger itself into a loop.
  var pending = null;
  var observer = new MutationObserver(scheduleScan);

  function scan() {
    observer.disconnect();
    try {
      initAllAccordions();
      syncBubble();
      if (document.querySelector(DRAWER_HEADER)) {
        if (cartCount === null) {
          refreshCount();
        } else {
          paintLabel();
        }
      }
    } catch (e) {
      // never let a scan take the page down
    }
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  function scheduleScan() {
    if (pending) return;
    pending = window.setTimeout(function () {
      pending = null;
      scan();
    }, 250);
  }

  // Refresh the count only when the cart actually changes or the drawer opens
  ['qsc2:drawer:opened', 'qsc2:cart:updated', 'cart:updated'].forEach(function (name) {
    document.addEventListener(name, refreshCount);
  });

  // Catch-all: any app or script that hits a Shopify cart endpoint triggers a
  // refresh. This is what keeps the counter correct when Qikify (or any other
  // app) changes the cart without firing an event we listen for.
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      var isCartWrite = /\/cart\/(add|change|update|clear)/.test(url);

      return nativeFetch.apply(this, arguments).then(function (response) {
        if (isCartWrite) window.setTimeout(refreshCount, 150);
        return response;
      });
    };
  }

  // Restoring from the back/forward cache serves a stale page — re-check
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refreshCount();
  });

  // Re-bind accordions when a section is re-rendered in the theme editor
  document.addEventListener('shopify:section:load', function (event) {
    initAllAccordions(event.target);
  });

  /* ========================================================================
     3. ADD TO CART — delegated, so it survives section re-renders

     custom-cart-drawer.liquid binds its add-to-cart handler to whatever forms
     exist at DOMContentLoaded. The theme editor re-renders the product section
     on every change, which replaces that form — the handler goes with it, Dawn
     takes over, and because cart-notification is stubbed out nothing opens and
     the button spins forever.

     Binding on `document` in the CAPTURE phase fixes that permanently: it runs
     before any handler on the form itself, works for forms added later, and
     always clears the spinner.
     ======================================================================== */

  function clearSubmitState(form) {
    try {
      var btn = form.querySelector('[type="submit"]');
      if (!btn) return;
      btn.classList.remove('loading');
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('disabled');
      var sp = form.querySelector('.loading__spinner, .loading-overlay__spinner');
      if (sp) sp.classList.add('hidden');
    } catch (e) {}
  }

  function openSlideCart() {
    if (typeof window.openCustomCart === 'function') { window.openCustomCart(); return; }
    var link = document.querySelector('#cart-icon-bubble, a[href="/cart"]');
    if (link) { link.click(); return; }
    window.location.href = '/cart';
  }

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target && e.target.closest ? e.target.closest('form[action*="/cart/add"]') : null;
      if (!form) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
        .then(function (r) { return r.json(); })
        .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
        .then(function (cart) {
          cartCount = cart.item_count;
          syncBubble();
          openSlideCart();
        })
        .catch(function (err) { console.error('Add to cart failed:', err); })
        .then(function () { clearSubmitState(form); });
    },
    true
  );

  // Belt and braces: never leave a spinner running after a section re-render
  document.addEventListener('shopify:section:load', function (event) {
    var forms = (event.target || document).querySelectorAll('form[action*="/cart/add"]');
    Array.prototype.forEach.call(forms, clearSubmitState);
  });

  function boot() {
    initAllAccordions();
    refreshCount();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();